/**
 * @file React Query hooks for the Research dashboard feature (86d3cefah).
 *
 * Mirrors the src/hooks/useXoe.ts convention: flat exports, `['research', ...]`
 * query-key shape, mutations invalidate `['research','history']`.
 *
 * Query-key shape:
 *   knowledge search   → ['research', 'knowledge', query, sources]
 *   literature search  → ['research', 'literature', q, sort, page, size, filters, facets]
 *   literature article → ['research', 'literature', 'article', pmid]
 *   search history     → ['research', 'history', limit?, source?]
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import {
	searchKnowledge,
	searchLiterature,
	getLiteratureArticle,
	listResearchSearches,
	saveResearchSearch,
	deleteResearchSearch,
	clearResearchSearches,
} from '../api/research/researchApi';
import {
	KnowledgeSearchResponse,
	LiteratureArticleResponse,
	LiteratureSearchParams,
	LiteratureSearchResponse,
	ListResearchSearchesParams,
	ResearchSearch,
	ResearchSearchSource,
	SaveResearchSearchPayload,
} from '../types/research/ResearchTypes';

// ── Knowledge search ────────────────────────────────────────────────────────────

/**
 * Search the clinic knowledge base. Disabled until the query has real content, so a
 * blank dashboard makes no request.
 *
 * `record` controls server-side history auto-recording (see KnowledgeSearchParams.record). It is
 * deliberately NOT in the query key: it changes a side effect, not the results, so keying on it
 * would split the cache for identical data.
 *
 * `keepPreviousData` holds the previous results while the next query loads — the dashboard renders
 * this beside the literature results, which do the same, so without it this pane would blank on
 * every keystroke while the other held.
 *
 * queryKey: ['research', 'knowledge', query, sources]
 */
export const useKnowledgeSearch = (
	query: string,
	sources: string[] = [],
	enabled = true,
	record = true,
) => {
	return useQuery<KnowledgeSearchResponse>({
		queryKey: ['research', 'knowledge', query, sources],
		queryFn: () => searchKnowledge({ q: query, sources, record }),
		enabled: enabled && query.trim().length > 0,
		placeholderData: keepPreviousData,
	});
};

// ── Literature search ───────────────────────────────────────────────────────────

/**
 * Search medical literature (MedQuery). The `available: false` envelope is a normal
 * result, not an error — consumers render the notice from the resolved data.
 *
 * Every dimension the request varies on is in the key, so a page/sort/filter/facet change is
 * a distinct cache entry. `params.record` is excluded for the same reason as in
 * useKnowledgeSearch — it changes a side effect, not the results.
 *
 * `placeholderData: keepPreviousData` holds the previous page on screen while the next one
 * loads, so paging never flashes an empty list.
 *
 * queryKey: ['research', 'literature', q, sort, page, size, filters, facets]
 */
export const useLiteratureSearch = (params: LiteratureSearchParams, enabled = true) => {
	return useQuery<LiteratureSearchResponse>({
		queryKey: [
			'research',
			'literature',
			params.q,
			params.sort ?? null,
			params.page ?? null,
			params.size ?? null,
			params.filters ?? [],
			params.facets ?? [],
		],
		queryFn: () => searchLiterature(params),
		enabled: enabled && params.q.trim().length > 0,
		placeholderData: keepPreviousData,
	});
};

// ── Literature article (detail view) ────────────────────────────────────────────

/**
 * Fetch one article's full record. Disabled until a pmid is selected, so the drawer only
 * fetches when it is actually opened. Cached generously — a published article record does
 * not change.
 *
 * A not-found pmid resolves to `{ available: false, message }` (not an error) — consumers
 * render that message rather than an error state.
 *
 * queryKey: ['research', 'literature', 'article', pmid]
 */
export const useLiteratureArticle = (pmid: string | null) => {
	return useQuery<LiteratureArticleResponse>({
		queryKey: ['research', 'literature', 'article', pmid],
		queryFn: () => getLiteratureArticle(pmid as string),
		enabled: !!pmid,
		staleTime: 60 * 60 * 1000, // 1h — article records are stable
		gcTime: 24 * 60 * 60 * 1000,
	});
};

// ── Search history ──────────────────────────────────────────────────────────────

/**
 * List the practitioner's recent research searches.
 *
 * queryKey: ['research', 'history', limit ?? null, source ?? null]
 */
export const useResearchHistory = (params: ListResearchSearchesParams = {}) => {
	return useQuery<ResearchSearch[]>({
		queryKey: ['research', 'history', params.limit ?? null, params.source ?? null],
		queryFn: () => listResearchSearches(params),
	});
};

// ── Save search (record history) ────────────────────────────────────────────────

/**
 * Persist a recent-search entry. On success invalidates the history list so the
 * Recent searches rail refreshes.
 */
export const useSaveResearchSearch = () => {
	const queryClient = useQueryClient();

	return useMutation<ResearchSearch, Error, SaveResearchSearchPayload>({
		mutationFn: (vars) => saveResearchSearch(vars),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['research', 'history'] });
		},
	});
};

// ── Delete one ──────────────────────────────────────────────────────────────────

/**
 * Delete a single recent-search entry.
 * On success invalidates the history list.
 */
export const useDeleteResearchSearch = () => {
	const queryClient = useQueryClient();

	return useMutation<void, Error, { id: number }>({
		mutationFn: (vars) => deleteResearchSearch(vars.id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['research', 'history'] });
		},
	});
};

// ── Clear all ────────────────────────────────────────────────────────────────────

/**
 * Clear the practitioner's entire recent-search history.
 * On success invalidates the history list.
 */
export const useClearResearchSearches = () => {
	const queryClient = useQueryClient();

	return useMutation<number, Error, void>({
		mutationFn: () => clearResearchSearches(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['research', 'history'] });
		},
	});
};

export type { ResearchSearchSource };
